import React, { useState, useRef, useEffect } from "react";
import styles from './SearchableDropdown.module.css';

const SearchableDropdown = ({ placeholder, data, productTitle, filterKey, onItemSelect, invoiceDataMaster }) => {
    const [query, setQuery] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Add "Add New Service / Item" as the first suggestion
    const filteredData = [
        { [filterKey]: "Add New Service / Item" },
        ...data.filter(
            (item) =>
                item[filterKey] &&
                item[filterKey]?.toLowerCase()?.includes(query?.toLowerCase())
        ),
    ];
    useEffect(() => {
        setQuery(productTitle)
    }, [productTitle])

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsFocused(false);
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
        };
    }, []);
    

    const handleKeyDown = (e) => {
        if (!isFocused) return;

        switch (e.key) {
            case "ArrowDown":
                setHighlightedIndex((prev) =>
                    prev < filteredData.length - 1 ? prev + 1 : 0
                );
                break;
            case "ArrowUp":
                setHighlightedIndex((prev) =>
                    prev > 0 ? prev - 1 : filteredData.length - 1
                );
                break;
            case "Enter":
                e.preventDefault();
                if (highlightedIndex === 0) {
                    // If "Add New Service / Item" is selected, pass the query as new item
                    handleSelect({ [filterKey]: query || "Add New Service / Item" });
                } else if (
                    highlightedIndex > 0 &&
                    highlightedIndex < filteredData.length
                ) {
                    handleSelect(filteredData[highlightedIndex]);
                } else if (query.trim() !== "") {
                    // If query is not empty, pass the manually typed value as the selection
                    handleSelect({ [filterKey]: query });
                }
                break;
            case "Escape":
                setIsFocused(false);
                setQuery("");
                break;
            default:
                break;
        }
    };

    const handleSelect = (item) => {
        setQuery(item[filterKey]);
        setIsFocused(false);
        onItemSelect(item);
    };

    const isProductType = (itemType) => {
        const normalized = (itemType || "").toLowerCase();
        return normalized === "product" || normalized === "products";
    };

    const getSuggestionTooltip = (item) => {
        if (!item || item[filterKey] === "Add New Service / Item") return "";

        if (isProductType(item?.itemType)) {
            const base = item?.basePrice ?? item?.price ?? "0";
            const selling = item?.disPrice ?? item?.price ?? "0";
            return `Product • Base: ₹${base} • Selling: ₹${selling}`;
        }

        const price = item?.price ?? "0";
        return `Service • Price: ₹${price}`;
    };

    const handleBlur = (e) => {
        const nextFocusedElement = e.relatedTarget;
        if (nextFocusedElement && dropdownRef.current?.contains(nextFocusedElement)) {
            return;
        }
        setIsFocused(false);
        setHighlightedIndex(-1);
    };

    // Find the last product in the list // this is to handle auto focus
    const lastProduct = invoiceDataMaster?.productData?.slice(-1)[0];

    return (
        <div
            ref={dropdownRef}
            className={styles.dropdownContainer}
            onBlur={handleBlur}
            tabIndex={0}
        >
            <input
                type="text"
                ref={inputRef}
                placeholder={placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                className={styles.searchInput}
                autoFocus={lastProduct?.identifier!=='' ? false : true }
            />
            {isFocused && (
                <div className={styles.suggestionsList}>
                    {filteredData.map((item, index) => (
                        <div
                            key={index}
                            className={`${styles.suggestionItem} ${index === highlightedIndex ? styles.highlighted : ""
                                }`}
                            title={getSuggestionTooltip(item)}
                            onMouseDown={() => handleSelect(item)} // Prevent onBlur from triggering
                        >
                            <span>{item[filterKey]}</span>
                            {item?.itemType && item[filterKey] !== "Add New Service / Item" && (
                                <span className={styles.itemTypeTag}>
                                    {isProductType(item.itemType) ? "Product" : "Service"}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
